// ---------------------------------------------------------------------------
// WorkspaceDesignSystemsList — the list the inventory banner counts.
//
// User-perspective audit (2026-08-04, production): the banner said
// "48 design systems in this workspace" and its own copy promised "Opening
// this screen lists them" — but nothing on the screen rendered them, and
// /studio/library reported "SAVED WORK 0" for the same account. The count
// was measured; the objects were invisible. This component renders the SAME
// read the count comes from (`useDesignSystemInventory().systems`), so the
// number and the rows can only agree.
//
// Progressive disclosure: the first PREVIEW_COUNT rows render; the rest sit
// behind one "Show all N" action. Each row leads with the system's DESIGN
// (axis swatches from the saved 6-axis snapshot) and offers one action —
// load it into the theme editor. Systems saved without an axis snapshot get
// an honestly-disabled action with the reason visible, never a dead click.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { ChevronDown, ChevronUp, Palette } from 'lucide-react'
import type { DesignSystem } from '@cogo/kg-repository'
import { AXIS_KEYS, type DesignSystemState } from '../lib/design-system-options'
import { parseDesignSystemState } from '../lib/theme-tokens'
import { AxisValueSwatch } from './axis-visuals'

const PREVIEW_COUNT = 6

const NO_SNAPSHOT_REASON =
  'Saved without an axis snapshot — nothing to load into the editor.'

/**
 * The saved snapshot, but only when it is one the editor can actually load:
 * all six axes present and every value a real option on its axis. Anything
 * else yields `{}`, which is what disables the row's action.
 *
 * The gate is `parseDesignSystemState` — the same parser the rest of Studio
 * uses to read a persisted six-axis state — rather than a local filter, so
 * "loadable" means one thing in one place. A looser rule would be actively
 * dishonest here: `tokens` is a free-form JSON column, and a row holding a
 * computed ThemeTokens payload carries a `radius` of `"0.625rem"` — a CSS
 * length, not a radius axis option. Accepting that string would enable the
 * button and then push a value the radius axis cannot represent, trading
 * the old "nothing loads" defect for a quieter one.
 */
export function editorAxesOf(
  system: DesignSystem,
): Partial<DesignSystemState> {
  const state = parseDesignSystemState(system.axes)
  if (!state) return {}
  const known: Partial<DesignSystemState> = {}
  for (const key of AXIS_KEYS) {
    ;(known as Record<string, string | undefined>)[key] = state[key]
  }
  return known
}

export interface WorkspaceDesignSystemsListProps {
  systems: readonly DesignSystem[]
  /** Loads a saved snapshot into the theme editor (axis → value). */
  onApply: (axes: Partial<DesignSystemState>) => void
  /** Reports which saved system became the editor's source context. */
  onSelectSystem?: (system: DesignSystem) => void
}

export function WorkspaceDesignSystemsList({
  systems,
  onApply,
  onSelectSystem,
}: WorkspaceDesignSystemsListProps) {
  const [showAll, setShowAll] = useState(false)

  if (systems.length === 0) return null

  const visible = showAll ? systems : systems.slice(0, PREVIEW_COUNT)
  const hiddenCount = systems.length - visible.length

  return (
    <section
      data-testid="workspace-design-systems-list"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-bold text-foreground">
          My design systems
          <span className="ml-2 text-sm font-medium text-muted-foreground">
            {systems.length}
          </span>
        </h2>
        {/* Scope line — reconciles this surface with /studio/library, which
            reads a DIFFERENT registry (saved work items). Without it the two
            screens appear to contradict each other ("48 here" vs "0 there"). */}
        <p className="text-xs leading-5 text-muted-foreground">
          Saved from the theme editor to this workspace. Library lists saved
          work items — a separate collection.
        </p>
      </header>

      <ul className="mt-3 divide-y divide-border/70">
        {visible.map((system) => {
          const axes = editorAxesOf(system)
          const hasSnapshot = Object.keys(axes).length > 0
          return (
            <li
              key={system.id}
              data-testid="workspace-design-system-row"
              className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {system.name}
                </span>
                <span className="block truncate font-mono text-xs text-muted-foreground">
                  {system.id}
                </span>
              </span>
              <span className="flex items-center gap-2">
                {(['baseColor', 'theme', 'chart', 'radius', 'font'] as const).map(
                  (axis) =>
                    axes[axis] ? (
                      <AxisValueSwatch key={axis} axis={axis} value={axes[axis]} />
                    ) : null,
                )}
              </span>
              <button
                type="button"
                data-testid="workspace-design-system-apply"
                disabled={!hasSnapshot}
                title={hasSnapshot ? undefined : NO_SNAPSHOT_REASON}
                onClick={() => {
                  onApply(axes)
                  onSelectSystem?.(system)
                }}
                className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Palette className="h-3.5 w-3.5" aria-hidden />
                Load in editor
              </button>
              {!hasSnapshot ? (
                <span className="basis-full text-xs leading-5 text-muted-foreground">
                  {NO_SNAPSHOT_REASON}
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>

      {hiddenCount > 0 || showAll ? (
        <button
          type="button"
          data-testid="workspace-design-systems-toggle"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 inline-flex min-h-10 items-center gap-1 rounded-lg px-2 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted"
        >
          {showAll ? (
            <>
              Show fewer
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            </>
          ) : (
            <>
              Show all {systems.length}
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </>
          )}
        </button>
      ) : null}
    </section>
  )
}
